import { useCallback, useEffect, useMemo } from 'react'
import { Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { OrderDirection, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_ALL_STUDY_GUIDES_LIST } from 'queries/studyGuide'
import { getWithSectionFilter } from '../../BlogPostList/helpers'
import { FindAllStudyGuidesList, FindAllStudyGuidesListVariables } from '../types'

type StudyGuidesQueryFilters = {
  platformId: string
  statusId: string
}

type UseStudyGuidesQueryOptions = {
  page: number
  take: number
  search: string
  platformId: string
  statusId: string
  onChange: (params: StudyGuidesQueryParams) => void
}

export type StudyGuidesQueryParams = Params<StudyGuidesQueryFilters>

const castStudyGuidesParams = ({
  page,
  take,
  search,
  filters,
}: StudyGuidesQueryParams): FindAllStudyGuidesListVariables => {
  const withSection = filters.statusId ? getWithSectionFilter(filters.statusId) : undefined
  const platformId = filters.platformId ? { id: filters.platformId } : undefined
  const querySearch = search ? { query: search, type: SearchTypeEnum.ILike } : undefined
  const skip = (page - 1) * take

  return {
    query: {
      skip,
      take,
      relations: ['section'],
      search: querySearch,
      where: {
        platform: platformId,
        withSection,
      },
      order: {
        updatedAt: OrderDirection.DESC,
      },
    },
  }
}

export const useStudyGuidesQuery = ({
  page,
  take,
  search,
  platformId,
  statusId,
  onChange,
}: UseStudyGuidesQueryOptions) => {
  const queryParams = useMemo(
    () => ({
      page,
      take,
      search,
      filters: {
        platformId,
        statusId,
      },
    }),
    [page, platformId, search, statusId, take]
  )

  const studyGuidesQuery = useQueryWithParams<
    FindAllStudyGuidesList,
    FindAllStudyGuidesListVariables,
    'findAllStudyGuide',
    'items',
    'count',
    StudyGuidesQueryFilters
  >(FIND_ALL_STUDY_GUIDES_LIST, 'findAllStudyGuide', 'items', 'count', castStudyGuidesParams, onChange, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    void studyGuidesQuery.refetch(queryParams)
  }, [queryParams, studyGuidesQuery.refetch])

  const refetchCurrent = useCallback(
    () => studyGuidesQuery.refetch(queryParams),
    [queryParams, studyGuidesQuery.refetch]
  )

  return {
    ...studyGuidesQuery,
    refetchCurrent,
  }
}
